/**
 * Report Editor - iframe + designMode WYSIWYG editor
 *
 * Uses an iframe with document.designMode to provide rich text editing
 * while preserving all HTML structure and CSS from the generated report.
 */

import { useRef, useEffect, useCallback, useState } from 'react';

interface ReportEditorProps {
  htmlContent: string;
  onSave: (html: string) => void;
  onCancel: () => void;
  isSaving: boolean;
}

type FormatType = 'p' | 'h1' | 'h2' | 'h3';

const FORMAT_OPTIONS: { value: FormatType; label: string }[] = [
  { value: 'p', label: 'Normaal' },
  { value: 'h1', label: 'Kop 1' },
  { value: 'h2', label: 'Kop 2' },
  { value: 'h3', label: 'Kop 3' },
];

export function ReportEditor({ htmlContent, onSave, onCancel, isSaving }: ReportEditorProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const initRef = useRef(false);
  const [activeStyles, setActiveStyles] = useState({
    bold: false,
    italic: false,
    underline: false,
  });
  const [currentFormat, setCurrentFormat] = useState<FormatType>('p');

  // Initialize iframe with content and enable designMode
  useEffect(() => {
    if (initRef.current) return;
    const iframe = iframeRef.current;
    if (!iframe) return;

    const handleLoad = () => {
      const doc = iframe.contentDocument;
      if (!doc || initRef.current) return;
      initRef.current = true;

      doc.open();
      doc.write(htmlContent);
      doc.close();
      doc.designMode = 'on';

      // Add editor-specific styles for cursor and selection
      const editorStyle = doc.createElement('style');
      editorStyle.setAttribute('data-editor-style', 'true');
      editorStyle.textContent = `
        body { cursor: text; }
        ::selection { background: #3b82f6; color: white; }
      `;
      doc.head.appendChild(editorStyle);

      // Listen for selection changes to update toolbar state
      doc.addEventListener('selectionchange', updateToolbarState);
      doc.addEventListener('keyup', updateToolbarState);
      doc.addEventListener('mouseup', updateToolbarState);

      // Auto-resize
      resizeIframe();
    };

    iframe.addEventListener('load', handleLoad);
    // Trigger load for about:blank
    iframe.src = 'about:blank';

    return () => {
      iframe.removeEventListener('load', handleLoad);
    };
  }, [htmlContent]);

  // Resize iframe to match content
  const resizeIframe = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe?.contentDocument?.body) return;
    const height = iframe.contentDocument.documentElement.scrollHeight;
    iframe.style.height = `${Math.max(height + 40, 400)}px`;
  }, []);

  // Periodic resize to handle content changes
  useEffect(() => {
    const interval = setInterval(resizeIframe, 500);
    return () => clearInterval(interval);
  }, [resizeIframe]);

  // Update toolbar active state based on current selection
  const updateToolbarState = useCallback(() => {
    const doc = iframeRef.current?.contentDocument;
    if (!doc) return;

    setActiveStyles({
      bold: doc.queryCommandState('bold'),
      italic: doc.queryCommandState('italic'),
      underline: doc.queryCommandState('underline'),
    });

    // Detect current block format
    const block = doc.queryCommandValue('formatBlock');
    if (block === 'h1') setCurrentFormat('h1');
    else if (block === 'h2') setCurrentFormat('h2');
    else if (block === 'h3') setCurrentFormat('h3');
    else setCurrentFormat('p');
  }, []);

  // Execute command on the iframe document
  const execCommand = useCallback((command: string, value?: string) => {
    const doc = iframeRef.current?.contentDocument;
    if (!doc) return;
    doc.execCommand(command, false, value);
    updateToolbarState();
  }, [updateToolbarState]);

  // Extract HTML from iframe for saving
  const handleSave = useCallback(() => {
    const doc = iframeRef.current?.contentDocument;
    if (!doc) return;

    // Remove editor-injected styles
    const editorStyles = doc.querySelectorAll('[data-editor-style]');
    editorStyles.forEach((el) => el.remove());

    const html = '<!DOCTYPE html>\n' + doc.documentElement.outerHTML;

    onSave(html);
  }, [onSave]);

  // Toolbar button helper - uses onMouseDown to prevent stealing focus from iframe
  const ToolbarButton = ({
    onClick,
    active,
    title,
    children,
  }: {
    onClick: () => void;
    active?: boolean;
    title: string;
    children: React.ReactNode;
  }) => (
    <button
      type="button"
      onMouseDown={(e) => {
        e.preventDefault();
        onClick();
      }}
      title={title}
      className={`px-2.5 py-1.5 rounded text-sm font-medium transition-colors ${
        active
          ? 'bg-blue-100 text-blue-700 border border-blue-300'
          : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
      }`}
    >
      {children}
    </button>
  );

  return (
    <div className="min-h-screen bg-gray-100 p-4">
      <div className="max-w-5xl mx-auto">
        {/* Header with Save/Cancel */}
        <div className="bg-white rounded-lg shadow-lg p-4 mb-4 flex items-center justify-between">
          <h1 className="text-lg font-semibold text-gray-900">Verslag Bewerken</h1>
          <div className="flex gap-2">
            <button
              onClick={onCancel}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
              Annuleren
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              {isSaving ? (
                <>
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Opslaan...
                </>
              ) : (
                'Opslaan'
              )}
            </button>
          </div>
        </div>

        {/* Toolbar */}
        <div className="bg-white rounded-lg shadow-lg p-2 mb-4 flex items-center gap-1 flex-wrap">
          {/* Format dropdown */}
          <select
            value={currentFormat}
            onMouseDown={(e) => e.stopPropagation()}
            onChange={(e) => {
              const value = e.target.value as FormatType;
              execCommand('formatBlock', `<${value}>`);
              setCurrentFormat(value);
            }}
            className="px-2 py-1.5 rounded border border-gray-300 text-sm bg-white text-gray-700 cursor-pointer"
          >
            {FORMAT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>

          <div className="w-px h-6 bg-gray-300 mx-1" />

          {/* Text formatting */}
          <ToolbarButton
            onClick={() => execCommand('bold')}
            active={activeStyles.bold}
            title="Vet (Ctrl+B)"
          >
            <strong>B</strong>
          </ToolbarButton>
          <ToolbarButton
            onClick={() => execCommand('italic')}
            active={activeStyles.italic}
            title="Cursief (Ctrl+I)"
          >
            <em>I</em>
          </ToolbarButton>
          <ToolbarButton
            onClick={() => execCommand('underline')}
            active={activeStyles.underline}
            title="Onderstrepen (Ctrl+U)"
          >
            <u>U</u>
          </ToolbarButton>

          <div className="w-px h-6 bg-gray-300 mx-1" />

          {/* Lists */}
          <ToolbarButton
            onClick={() => execCommand('insertUnorderedList')}
            title="Ongeordende lijst"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </ToolbarButton>
          <ToolbarButton
            onClick={() => execCommand('insertOrderedList')}
            title="Geordende lijst"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              <text x="1" y="7" fontSize="6" fill="currentColor" stroke="none" fontFamily="sans-serif">1</text>
              <text x="1" y="13" fontSize="6" fill="currentColor" stroke="none" fontFamily="sans-serif">2</text>
              <text x="1" y="19" fontSize="6" fill="currentColor" stroke="none" fontFamily="sans-serif">3</text>
            </svg>
          </ToolbarButton>

          <div className="w-px h-6 bg-gray-300 mx-1" />

          {/* Undo/Redo */}
          <ToolbarButton onClick={() => execCommand('undo')} title="Ongedaan maken (Ctrl+Z)">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a5 5 0 015 5v2M3 10l4-4M3 10l4 4" />
            </svg>
          </ToolbarButton>
          <ToolbarButton onClick={() => execCommand('redo')} title="Opnieuw (Ctrl+Y)">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 10H11a5 5 0 00-5 5v2M21 10l-4-4M21 10l-4 4" />
            </svg>
          </ToolbarButton>
        </div>

        {/* Editor iframe */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <iframe
            ref={iframeRef}
            className="w-full border-0"
            style={{ minHeight: '400px' }}
            title="Report Editor"
          />
        </div>
      </div>
    </div>
  );
}
